import { Component, OnDestroy, OnInit } from "@angular/core";
import { FormBuilder, FormGroup } from "@angular/forms";
import { AccessControlService } from "app/access-control/access-control-service";
import {
  AlertController,
  Events,
  IonicPage,
  LoadingController,
  ModalController,
  NavController,
  NavParams,
  PopoverController,
  ToastController,
  Toggle,
} from "ionic-angular";
import SortOptionsMedications from "models/content-wrapper/sort-options-medications";
import { Action } from "models/order-management/action";
import { OrderErrorResponse } from "models/order-management/errorResponse";
import { OrderResponse } from "models/order-management/models";
import { OrderComponent } from "models/order-management/orderComponent";
import CredentialedResource from "models/resource-management/credentialed-resource";
import { PatientResponseDto } from "models/scriptSure/patient-response-dto";
import * as moment from "moment-timezone";
import { NavigationService } from "navigation/navigation.service";
import { CareTeamsService } from "providers/care-teams-service/care-teams-service";
import { NavBarServiceProvider } from "providers/nav-bar-service/nav-bar-service";
import {
  PatientChartIdChangeSubscription,
  subscribeToPatientChartIdChange,
} from "providers/nav-bar-service/patient-chart-id-change";
import { OrderManagementDataService } from "providers/order-management-data-service/order-management-data-service";
import { PatientMedicationsService } from "providers/patient-charts/patient-medications.service";
import { ReferralsServiceProvider } from "providers/patient-charts/referrals-service";
import { forkJoin } from "rxjs/observable/forkJoin";
import { take } from "rxjs/operators";
import Utils from "utils/Utilities";
import { AuthService } from "../../../app/auth/auth.service";
import { EditViewPopoverPage } from "../../../navigation/components/edit-view-popover/edit-view-popover";
import { OrderDataService } from "../../../providers/order-data-service/order-data-service";
import { ReportExecutionService } from "../../../providers/report-execution-service/report-execution.service";
import { ResourceManagementService } from "../../../providers/resource-management/resource-management.service";
import { StateManagementService } from "../../../providers/state-management/state-management.service";
import { BasePage } from "../../base-page";
import MedicationDiscontinue from "./medication-discontinue/medication-discontinue";
import MedicationEditPage from "./medication-edit/medication-edit";
import {
  ConvertedToOrder,
  MedicationSearchResults,
  MedicationStatusUpdate,
  PatientMedication,
} from "./patient-medication";

@IonicPage({
  name: "patient-medications",
  segment: "patient-medications/:patientId",
})
@Component({
  selector: "patient-medications",
  templateUrl: "medications.html",
})
export class MedicationsPage extends BasePage implements OnInit, OnDestroy {
  // paging
  sortOptions: SortOptionsMedications = {
    column: "name",
    name: "asc",
    dosage: "asc",
    route: "asc",
    frequency: "asc",
    medEntryDate: "asc",
    orderDate: "asc",
    converted: "asc",
  };
  totalPages: number;
  currentPage: number = 0;
  from: number = 0;
  to: number = 9;
  pageSize: number = 10;
  pageType: string = "";
  medicationSearchResults: MedicationSearchResults;
  medication: PatientMedication;
  selectedMedication: PatientMedication = null;
  spinner = null;
  spinnerCounter = 0;
  selectPrintOptions: any[];
  printOptions: any[] = [
    { displayName: "Print All", key: "print_all" },
    { displayName: "Print Filtered", key: "print_filtered" },
  ];
  isPalliative: boolean;
  credentialedResource: CredentialedResource;
  userDisplayName: string;

  searchForm: FormGroup;
  error: boolean = false;
  orderTypeConfigurations: OrderComponent;

  teamId = 0;
  teamQbosId = 0;
  teamName = "";
  truncateLength = 300;
  initialTruncateLength = 300;

  fromDate: any = null;
  toDate: any = null;

  discontinuedOrdersEnabled: boolean = false;
  order: OrderResponse;

  scriptSurePatient: PatientResponseDto;

  // NOVA-1645 — URL :id → re-fetch subscription (see patient-chart-id-change.ts)
  private patientCtxSub: PatientChartIdChangeSubscription;

  constructor(
    public navCtrl: NavController,
    public navSvc: NavigationService,
    public navParams: NavParams,
    public modalCtrl: ModalController,
    public loadingCtrl: LoadingController,
    private popoverCtrl: PopoverController,
    public accessCntrl: AccessControlService,
    private medicationService: PatientMedicationsService,
    public referralsService: ReferralsServiceProvider,
    private orderDataService: OrderDataService,
    private alertController: AlertController,
    private toastCtrl: ToastController,
    private resourceManagementService: ResourceManagementService,
    private fb: FormBuilder,
    state: StateManagementService,
    private reportExecutionService: ReportExecutionService,
    private popover: PopoverController,
    public stateData: StateManagementService,
    private orderManagementDataService: OrderManagementDataService,
    private careTeamService: CareTeamsService,
    private navBarService: NavBarServiceProvider,
    private authSvc: AuthService,
    public events: Events
  ) {
    super(state);

    this.referralsService.ACTIVE_TAB_NAME = "medications";
    if (this.referralsService.PATIENT_STATUS) {
      this.pageType = this.referralsService.PATIENT_STATUS;
    }

    this.searchForm = this.fb.group({
      fromDate: this.fb.control(""),
      toDate: this.fb.control(""),
      source: this.fb.control(null),
      status: this.fb.control(null),
      q: this.fb.control(""),
    });
    this.isPalliative =
      this.stateData.patientCareType &&
      this.stateData.patientCareType.toLowerCase() == "palliative";
  }

  ngOnInit(): void {
    this.careTeamService
      .getCareTeamMembers(this.navBarService.getExternalId().toString())
      .subscribe((data) => {
        this.teamName = data["team"].name;
        this.teamId = data["team"].id;
        this.teamQbosId = data["team"].qbosId;
      });

    // NOVA-1645 — Ionic reuses this page across a hash :id change. loadData()
    // is keyed on referralsService.PATIENT_ID (the internal chart id = URL id),
    // so on a URL id change sync that field and re-fetch. Set up in ngOnInit;
    // torn down in ngOnDestroy.
    if (!this.patientCtxSub) {
      this.patientCtxSub = subscribeToPatientChartIdChange(
        this.events,
        this.navBarService,
        {
          getBoundId: () => this.referralsService.PATIENT_ID,
          onChange: (urlId) => {
            this.referralsService.PATIENT_ID = urlId;
            this.loadData();
          },
        }
      );
    }
  }

  ngOnDestroy(): void {
    // NOVA-1645 — tear down the specific handler ref (Events.unsubscribe by
    // topic alone would drop other subscribers on this topic).
    if (this.patientCtxSub) {
      this.patientCtxSub.unsubscribe();
      this.patientCtxSub = undefined;
    }
  }

  ionViewWillEnter(): void {
    this.loadData();
    this.makeSelectPrintOptions();
    this.getOrderTypeConfigurations();
    this.getUserInformation();
  }

  ionViewDidLoad(): void {
    this.navBarService.getScriptSurePatient().subscribe((scriptSurePatient) => {
      this.scriptSurePatient = scriptSurePatient;
    });
  }

  getOrderTypeConfigurations(): void {
    this.orderManagementDataService
      .getOrderTypes()
      .subscribe((orderTypeConfigurations) => {
        let medications = orderTypeConfigurations.filter(
          (orderType) => orderType.orderType == "MEDICATION"
        )[0];
        this.orderTypeConfigurations = medications.components.filter(
          (component) => component.componentName == "multi-option"
        )[0];
      }, OrderErrorResponse.handleError(this.toastCtrl, "Could not get order type configurations."));
  }

  trimString(string, length) {
    return string.length > length
      ? string.substring(0, length) + "..."
      : string;
  }

  loadData(): void {
    const sf = this.getMedicationFormFilter();

    if (
      Date.parse(this.searchForm.get("fromDate").value) >
      Date.parse(this.searchForm.get("toDate").value)
    ) {
      this.error = true;
      return;
    } else {
      this.error = false;
    }

    this.increaseSpinnerCount("Loading medications ...");
    const currentDate = moment(new Date()).format("YYYY-MM-DD");
    this.medicationService
      .getMedications(
        this.referralsService.PATIENT_ID,
        sf,
        this.sortOptions,
        currentDate
      )
      .pipe(take(1))
      .subscribe(
        (x: MedicationSearchResults) => {
          this.medicationSearchResults = x;
          this.medicationSearchResults.patientMedication =
            this.medicationSearchResults.patientMedication.map((medication) => {
              medication.showMoreRnNotes = false;
              medication.showMorePNotes = false;
              medication.showMoreDReason = false;
              medication.orderStatus = medication.orderStatus
                ? medication.orderStatus
                : "-";
              return medication;
            });
          this.totalPages = Math.ceil(x.totalRows / this.pageSize);
          this.decreaseSpinnerCount();
        },
        (err) => {
          this.decreaseSpinnerCount();
          let toast = this.toastCtrl.create({
            message: "Error loading medications",
            duration: 3000,
            position: "below",
            cssClass: "error",
          });

          toast.onDidDismiss(() => {});

          toast.present();
        }
      );
  }

  getMedicationFormFilter(
    from = this.from,
    to = this.to,
    source: string = undefined
  ) {
    let start = moment(this.searchForm.get("fromDate").value);
    let startString = start.isValid() ? start.toISOString() : "";
    let end = moment(this.searchForm.get("toDate").value);
    // Add 1 day to end date to make it inclusive
    let endString = end.isValid() ? end.add(1, "day").toISOString() : "";

    if (!source) {
      source = this.searchForm.get("source").value
        ? this.searchForm.get("source").value
        : "";
    }

    return {
      fromDate: startString,
      fromRow: from,
      searchString: this.searchForm.get("q").value,
      source,
      status: this.searchForm.get("status").value
        ? this.searchForm.get("status").value
        : "",
      toDate: endString,
      toRow: to,
      showDiscontinued: this.discontinuedOrdersEnabled,
    };
  }

  onArrowClicked(m: PatientMedication) {
    if (m.selected) {
      m.selected = false;
      return;
    }

    this.medicationSearchResults.patientMedication.forEach((x) => {
      x.selected = false;
    });

    this.increaseSpinnerCount("Loading medication ...");
    const currentDate = moment(new Date()).format("YYYY-MM-DD");
    this.medicationService
      .getMedication(m.patientChartId, m.entityId, currentDate)
      .pipe(take(1))
      .subscribe(
        (x: PatientMedication) => {
          m.selected = true;
          this.selectedMedication = x;
          this.decreaseSpinnerCount();
        },
        (err) => {
          this.decreaseSpinnerCount();
          console.error(err);
        }
      );
  }

  increaseSpinnerCount(message) {
    this.spinnerCounter++;
    if (this.spinnerCounter && this.spinner == null) {
      this.spinner = this.loadingCtrl.create({
        content: message,
      });
      this.spinner.present();
    }
  }

  decreaseSpinnerCount() {
    this.spinnerCounter--;
    if (this.spinnerCounter <= 0 && this.spinner) {
      this.spinner.dismiss();
      this.spinner = null;
    }
  }

  presentEditViewPopover(e: any, m: PatientMedication) {
    const popover = this.popoverCtrl.create(
      EditViewPopoverPage,
      {
        isOrder: m.isOrder,
        showMedicationEdit: true,
        isPatientChart: true,
        showOrderManagement: this.showOrderManagement,
        isPalliative: this.isPalliative,
        medication: m,
        ePrescribeEnabled: this.isEPrescribeEnabled(),
      },
      { cssClass: "edit-event-popover" }
    );

    // here i have to code for going to OE screen
    popover.onDidDismiss((mode) => {
      if (!mode) return;
      if (mode.edit) {
        this.increaseSpinnerCount("Loading Medication");
        let obs = [];
        obs.push(this.orderDataService.getMedicationAdminConfig());
        obs.push(
          this.referralsService.getPatientById(
            this.referralsService.PATIENT_ID,
            false
          )
        );
        const currentDate = moment(new Date()).format("YYYY-MM-DD");
        obs.push(
          this.medicationService.getMedication(
            this.referralsService.PATIENT_ID,
            m.entityId,
            currentDate
          )
        );
        let convert = mode.convertToOrder ? mode.convertToOrder : false;
        forkJoin(obs).subscribe(
          ([y, z, a]: [any, any, any, PatientMedication]) => {
            this.decreaseSpinnerCount();
            let popover = this.modalCtrl.create(
              MedicationEditPage,
              {
                externalId: this.referralsService.QBOS_PATIENT_CHART_ID,
                patientChartId: this.referralsService.PATIENT_ID,
                categories: this.orderTypeConfigurations,
                medicationConfiguration: y,
                patientChart: z,
                convertToOrder: convert,
                medication: a,
                showOrderManagement: this.showOrderManagement,
                credentialedResource: this.credentialedResource,
                userDisplayName: this.userDisplayName,
                teamId: this.teamId,
                teamQbosId: this.teamQbosId,
                teamName: this.teamName,
                medicationSearchResults: this.medicationSearchResults,
              },
              { cssClass: "medication-add" }
            );

            popover.present({
              //ev: $event
            });
            popover.onDidDismiss((mode) => {
              this.loadData();
            });
          },
          (err) => this.decreaseSpinnerCount()
        );
      } else if (mode.discontinue && !this.isPalliative) {
        this.increaseSpinnerCount("Loading Medication");
        const currentDate = moment(new Date()).format("YYYY-MM-DD");
        this.medicationService
          .getMedication(
            this.referralsService.PATIENT_ID,
            m.entityId,
            currentDate
          )
          .pipe(take(1))
          .subscribe(
            (x: PatientMedication) => {
              this.decreaseSpinnerCount();
              let popover = this.modalCtrl.create(
                MedicationDiscontinue,
                {
                  patientChartId: this.referralsService.PATIENT_ID,
                  medication: x,
                },
                { cssClass: "popup-medium" }
              );

              popover.present({
                //ev: $event
              });
              popover.onDidDismiss((mode) => {
                this.loadData();
              });
            },
            (err) => {
              console.error(err);
              this.decreaseSpinnerCount();
            }
          );
      } else if (mode.discontinue && this.isPalliative) {
        const currentDate = moment(new Date()).format("YYYY-MM-DD");
        let alert = this.alertController.create({
          title: "Discontinue Confirmation",
          message: "Are you sure you want to discontinue this medication?",
          buttons: [
            {
              text: "Cancel",
              handler: () => {},
            },
            {
              text: "Proceed",
              handler: () => {
                this.increaseSpinnerCount("Discontinuing medication ...");
                let statusUpdate: MedicationStatusUpdate = {
                  status: "Discontinued",
                };
                this.medicationService
                  .updateMedicationStatus(
                    this.referralsService.PATIENT_ID,
                    m.entityId,
                    statusUpdate,
                    currentDate
                  )
                  .pipe(take(1))
                  .subscribe(
                    (x) => {
                      this.decreaseSpinnerCount();
                      this.loadData();
                    },
                    (err) => {
                      this.decreaseSpinnerCount();
                      console.error(err);
                      let toast = this.toastCtrl.create({
                        message: "Error discontinuing medication",
                        duration: 3000,
                        position: "below",
                        cssClass: "error",
                      });
                      toast.onDidDismiss(() => {});
                      toast.present();
                    }
                  );
              },
            },
          ],
        });
        alert.present().then((r) => console.log("Handled"));
      } else if (mode.delete) {
        let alert = this.alertController.create({
          title: "Cancel Confirmation",
          message: "Are you sure you want to delete this medication?",
          buttons: [
            {
              text: "Cancel",
              handler: () => {},
            },
            {
              text: "Proceed",
              handler: () => {
                this.increaseSpinnerCount("Deleting medication ...");
                this.medicationService
                  .deleteMedication(
                    this.referralsService.PATIENT_ID,
                    m.entityId
                  )
                  .pipe(take(1))
                  .subscribe(
                    (x) => {
                      this.decreaseSpinnerCount();
                      this.loadData();
                    },
                    (err) => {
                      this.decreaseSpinnerCount();
                      console.error(err);
                      let toast = this.toastCtrl.create({
                        message: "Error deleting medication",
                        duration: 3000,
                        position: "below",
                        cssClass: "error",
                      });

                      toast.onDidDismiss(() => {});

                      toast.present();
                    }
                  );
              },
            },
          ],
        });
        alert.present().then((r) => console.log("Handled"));
      } else if (mode.convertToOrder || mode.convertToErx) {
        // Determine conversion type
        const isErx = mode.convertToErx;
        const convertedToOrder = isErx ? "ERX" : "NON-ERX";

        // Fire-and-forget: Update medication status in background
        const currentDate = moment(new Date()).format("YYYY-MM-DD");
        const statusUpdate: MedicationStatusUpdate = {
          status: m.orderStatus,
          convertedToOrder,
        };
        this.medicationService
          .updateMedicationStatus(
            this.referralsService.PATIENT_ID,
            m.entityId,
            statusUpdate,
            currentDate
          )
          .pipe(take(1))
          .subscribe({
            error: (err) => {
              console.error(err);
              const toast = this.toastCtrl.create({
                message: `Error updating medication status for ${
                  isErx ? "eRx" : "order"
                } conversion`,
                duration: 3000,
                position: "below",
                cssClass: "error",
              });
              toast.present();
            },
          });

        // Navigate immediately without waiting for API response
        const prefilledData = {
          patientChartId: this.referralsService.PATIENT_ID,
          orderType: "MEDICATION",
          nameDescription: m.name.replace(" - " + m.strength, ""),
          route: m.route,
          dosage: m.dosage,
          frequency: m.frequency,
          strength: m.strength,
          symptoms: m.symptoms,
          isFreeText: m?.isFreeText ? m.isFreeText : false,
        };
        localStorage.setItem("intakeMedsUrl", window.location.href);
        this.navSvc.goToInternalPage("OrderEntryOMPage", {
          prefilledData,
          patChartId: this.navBarService.getExternalId().toString(),
          assessmentId: this.stateData.getAssessmentId() || null,
          assessmentUuid: this.stateData.getAssessmentUuid() || null,
          ...(isErx && { showEprescribeIframe: true }),
        });
      }
    });
    popover.present({
      ev: e,
    });
  }

  clearFilter(): void {
    this.searchForm.get("fromDate").setValue("", { emitEvent: false });
    this.searchForm.get("toDate").setValue("", { emitEvent: false });
    this.searchForm.get("q").setValue("", { emitEvent: false });
    this.searchForm.get("status").setValue(null, { emitEvent: false });
    this.searchForm.get("source").setValue(null, { emitEvent: false });
    this.refresh();
  }

  refresh(): void {
    this.loadData();
  }

  addMedication(): void {
    let obs = [];
    obs.push(this.orderDataService.getMedicationAdminConfig());
    obs.push(
      this.referralsService.getPatientById(
        this.referralsService.PATIENT_ID,
        false
      )
    );
    obs.push(
      this.medicationService.getMedications(
        this.referralsService.PATIENT_ID,
        {
          fromDate: "",
          toDate: "",
          fromRow: 0,
          toRow: 100,
          searchString: "",
          source: "",
          status: "",
          showDiscontinued: false,
        },
        this.sortOptions,
        moment(new Date()).format("YYYY-MM-DD")
      )
    );
    this.increaseSpinnerCount("Creating Medication");
    forkJoin(obs).subscribe(
      ([x, y, z]: [any, any, MedicationSearchResults]) => {
        this.decreaseSpinnerCount();
        let popover = this.modalCtrl.create(
          MedicationEditPage,
          {
            patientChartId: this.referralsService.PATIENT_ID,
            categories: this.orderTypeConfigurations,
            convertToOrder: false,
            medicationConfiguration: x,
            patientChart: y,
            showOrderManagement: this.showOrderManagement,
            credentialedResource: this.credentialedResource,
            userDisplayName: this.userDisplayName,
            teamId: this.teamId,
            teamQbosId: this.teamQbosId,
            teamName: this.teamName,
            medicationSearchResults: z,
          },
          { cssClass: "medication-add" }
        );
        popover.present({});
        popover.onDidDismiss((mode) => {
          this.loadData();
        });
      },
      (err) => {
        this.decreaseSpinnerCount();
        console.error("Error loading resources");
      }
    );
  }

  onPageChange(selectedPage: number) {
    this.from = selectedPage * this.pageSize;
    this.to = selectedPage * this.pageSize + 9;
    this.currentPage = selectedPage;
    this.loadData();
  }

  onToggleDiscontinuedOrders(toggle: Toggle) {
    this.discontinuedOrdersEnabled = toggle.value;
    this.loadData();
  }

  sortBy(selectedColumn: string) {
    this.currentPage = 0;
    this.from = 0;
    this.to = 9;
    if (selectedColumn === "name") {
      if (this.sortOptions.column === selectedColumn) {
        this.sortOptions.name =
          this.sortOptions.name === "asc" ? "desc" : "asc";
      } else {
        this.sortOptions.column = selectedColumn;
        this.sortOptions.name = "asc";
      }
    } else if (selectedColumn === "dosage") {
      if (this.sortOptions.column === selectedColumn) {
        this.sortOptions.dosage =
          this.sortOptions.dosage === "asc" ? "desc" : "asc";
      } else {
        this.sortOptions.column = selectedColumn;
        this.sortOptions.dosage = "asc";
      }
    } else if (selectedColumn === "route") {
      if (this.sortOptions.column === selectedColumn) {
        this.sortOptions.route =
          this.sortOptions.route === "asc" ? "desc" : "asc";
      } else {
        this.sortOptions.column = selectedColumn;
        this.sortOptions.route = "asc";
      }
    } else if (selectedColumn === "frequency") {
      if (this.sortOptions.column === selectedColumn) {
        this.sortOptions.frequency =
          this.sortOptions.frequency === "asc" ? "desc" : "asc";
      } else {
        this.sortOptions.column = selectedColumn;
        this.sortOptions.frequency = "asc";
      }
    } else if (selectedColumn === "medEntryDate") {
      if (this.sortOptions.column === selectedColumn) {
        this.sortOptions.medEntryDate =
          this.sortOptions.medEntryDate === "asc" ? "desc" : "asc";
      } else {
        this.sortOptions.column = selectedColumn;
        this.sortOptions.medEntryDate = "asc";
      }
    } else if (selectedColumn === "orderDate") {
      if (this.sortOptions.column === selectedColumn) {
        this.sortOptions.orderDate =
          this.sortOptions.orderDate === "asc" ? "desc" : "asc";
      } else {
        this.sortOptions.column = selectedColumn;
        this.sortOptions.orderDate = "asc";
      }
    } else if (selectedColumn === "converted") {
      if (this.sortOptions.column === selectedColumn) {
        this.sortOptions.converted =
          this.sortOptions.converted === "asc" ? "desc" : "asc";
      } else {
        this.sortOptions.column = selectedColumn;
        this.sortOptions.converted = "asc";
      }
    }
    this.loadData();
  }

  makeSelectPrintOptions() {
    this.selectPrintOptions = this.printOptions.map((e) => {
      return { name: e.displayName, value: e, checked: true };
    });
  }

  openOptionsPopover(ev) {
    let options = [];
    options = this.selectPrintOptions.map((a) => ({ ...a }));
    const noSort = true;
    const searchable = false;
    const popover = this.popover.create(
      "CareTeamsSelectPage",
      {
        options,
        searchable,
        noSort,
        isMultiSelect: false,
        isDownload: true,
        isAutoWidth: true,
        containerWidth: 105,
      },
      { cssClass: "multiple-select-custom-popover" }
    );
    popover.onDidDismiss((option) => {
      if (option && option.key == "print_all") {
        this.downloadMedsReport(true);
      } else if (option && option.key == "print_filtered") {
        this.downloadMedsReport(false);
      }
    });
    popover.present({ ev });
  }

  downloadMedsReport(printAll: boolean) {
    console.log("download meds report");
    this.reportExecutionService.downloadReport(
      "MEDs_report",
      this.buildReportPayload(printAll),
      this.reportExecutionService.buildReportFilename("MEDs_report", "pdf")
    );
    this.trackMedOrders(printAll);
  }

  buildReportPayload(printAll: boolean) {
    const orgId = this.authSvc.getUser().tenantId;
    let payload = {
      pOrgID: orgId.toString(),
      pChartID: this.referralsService.PATIENT_ID.toString(),
      pStatus: this.searchForm.get("status").value
        ? this.searchForm.get("status").value
        : "",
      pSource: this.searchForm.get("source").value
        ? this.searchForm.get("source").value
        : "",
      pStartDate: this.searchForm.get("fromDate").value
        ? moment(this.searchForm.get("fromDate").value).format("YYYY-MM-DD")
        : "",
      pEndDate: this.searchForm.get("toDate").value
        ? moment(this.searchForm.get("toDate").value)
            .add(1, "day")
            .format("YYYY-MM-DD")
        : "",
      pPrintAll: printAll ? "All" : "",
      pCareType: this.stateData.patientCareType
        ? this.stateData.patientCareType
        : "Hospice",
      pSearch: this.searchForm.get("q").value
        ? this.searchForm.get("q").value
        : "",
      pTimeZone: this.getTimeZone(),
    };
    if (
      payload.pStatus == "" &&
      payload.pSource == "" &&
      payload.pStartDate == "" &&
      payload.pEndDate == "" &&
      payload.pSearch == ""
    ) {
      payload.pPrintAll = "All";
    }
    return payload;
  }

  trackMedOrders(printAll) {
    const sf = this.getMedicationFormFilter(0, 999);
    if (printAll) {
      sf.fromDate = "";
      sf.toDate = "";
      sf.status = "";
      sf.searchString = "";
    }
    const currentDate = moment(new Date()).format("YYYY-MM-DD");
    this.medicationService
      .getMedications(
        this.referralsService.PATIENT_ID,
        sf,
        this.sortOptions,
        currentDate
      )
      .pipe(take(1))
      .subscribe(
        (medOrders: MedicationSearchResults) => {
          if (medOrders.patientMedication.length > 0) {
            this.orderManagementDataService
              .bulkUpdateOrders(
                Utils.createTrackOrdersPayload(
                  medOrders.patientMedication.map((p) => ({
                    orderId: p.orderDetails.orderManagementOrderId,
                    medicationId: p.entityId,
                  })),
                  Action.ActionTypeEnum.Printed,
                  moment.tz.guess(),
                  this.userDisplayName
                )
              )
              .subscribe(
                () => {},
                () => {}
              );
          }
        },
        () => {}
      );
  }

  getTimeZone() {
    let timeZone = moment.tz.guess();
    let time = new Date();
    return moment(time).tz(timeZone).format("z");
  }

  // Physician Order Type
  getApprovalType(approvalType: string): string {
    const option = MedicationEditPage.verifiedOrderOptions.find(
      (option) => option.value === approvalType
    );
    return option ? option.display : null;
  }

  getUserInformation() {
    this.resourceManagementService
      .getCredentialedResourceByExternalId(this.authSvc.getUser().userId)
      .subscribe((provider) => {
        this.credentialedResource = provider;
        this.userDisplayName = provider.findDisplayName(new Date());
      });
  }

  showMore() {
    this.truncateLength = this.truncateLength == 300 ? 10000 : 300;
  }
  getCategory(category: string, otherDescription?: string) {
    // CO-3018 — null-guard before toLowerCase(); a null/undefined category
    // (an unmapped/blank order category) otherwise threw a TypeError. Mirrors
    // the null-safety of replaceEmptyData / getConvertedDisplayValue.
    if (!category) {
      return category;
    }
    if (category.toLowerCase() == "other") {
      return otherDescription;
    }
    return category;
  }

  fromDateHandler() {
    let fromDate = this.formatDate(this.searchForm.get("fromDate").value);
    let toDate = this.formatDate(this.searchForm.get("toDate").value);
    if (moment(toDate).isBefore(fromDate)) {
      this.searchForm.get("fromDate").setValue("");
      this.toastError("From Date must be less than To Date.");
    }
  }

  toDateHandler() {
    let fromDate = this.formatDate(this.searchForm.get("fromDate").value);
    let toDate = this.formatDate(this.searchForm.get("toDate").value);
    if (moment(fromDate).isAfter(toDate)) {
      this.searchForm.get("toDate").setValue("");
      this.toastError("To Date must be greater than From Date.");
    }
  }

  toastError(message: string) {
    let toast = this.toastCtrl.create({
      message: message,
      duration: 3000,
      position: "below",
      cssClass: "error",
    });
    toast.present().then();
  }

  formatDate(date) {
    if (date !== null) {
      return moment(date).format("YYYY-MM-DD");
    } else {
      return date;
    }
  }

  getConvertedDisplayValue(
    convertedToOrder: ConvertedToOrder | undefined
  ): string {
    if (convertedToOrder === "ERX") {
      return "Yes - To eRx";
    } else if (convertedToOrder === "NON-ERX") {
      return "Yes - To non-eRx";
    }
    return "No";
  }

  replaceEmptyData(value: string) {
    if (!value || value.trim() === "") {
      return "-";
    }
    return value;
  }

  onChangeDatepicker(event: any, control: string) {
    if (event) {
      let value = event.target.value;
      let valueFormatted = moment(value, "MM/DD/YYYY", true);
      if (valueFormatted.isValid()) {
        switch (control) {
          case "fromDate":
            this.fromDate = new Date(valueFormatted);
            this.searchForm.get("fromDate").setValue(this.fromDate);
            this.fromDateHandler();
            break;
          case "toDate":
            this.toDate = new Date(valueFormatted);
            this.searchForm.get("toDate").setValue(this.toDate);
            this.toDateHandler();
            break;
        }
        this.error = false;
      } else {
        this.error = true;
      }
    }
  }

  isEPrescribeEnabled(): boolean {
    return !!(
      this.navBarService.patientEPrescribeFeatureToggleOn() &&
      this.scriptSurePatient
    );
  }
}



// WEBPACK FOOTER //
// ./src/pages/patient-chart/medications/medications.ts